import React, { useEffect, useState } from 'react';
import { EmployeeType, OptionsType } from '../../Components/Types';
import { useForm, FormProvider, SubmitHandler } from "react-hook-form";
import { Table, TableRow, FieldName } from "../../Components/DashboardTable";
import { validatePhone, validateNINumber, validatePostcode } from '../../Components/FormGroup/Validator';
import { countryOptions, maxDriverAge, minDriverAge, noAccidentsOptions, titleOptions, toastConfig } from '../../Components/Constants';
import { getDateString } from '../../Components/Functions';
import { Header } from '../../Components/PageComponents';
import Button from '../../Components/Button';
import { useMsal } from '@azure/msal-react';
import styled from "styled-components";
import { Form } from 'reactstrap';
import TextField from 'Components/FormGroup/TextField';
import SelectField from 'Components/FormGroup/SelectField';
import DateField from 'Components/FormGroup/DateField';
import CheckBoxField from 'Components/FormGroup/CheckBoxField';
import { toast } from 'react-toastify';
import { setCurrentEmployee } from 'features/Auth-Slice';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from 'store';
import { EmployeeAPI } from 'API/EmployeeAPI';
import { OptionsAPI } from 'API/GeneralAPI';
import { useDebouncedCallback } from "use-debounce";

const FieldDataCell = styled.td`
    padding:0.8em;
    width: 50%;
`;

function PersonalDetails() {
    const [options, setOptions] = useState<OptionsType[]>([]);
    const methods = useForm<EmployeeType>();
    const dispatch = useDispatch();
    const { instance } = useMsal();
    
    const tableRowHeight = "4em";
    
    const maxDriverAgeDate = new Date();
    maxDriverAgeDate.setFullYear(maxDriverAgeDate.getFullYear() - maxDriverAge);

    const minDriverAgeDate = new Date();
    minDriverAgeDate.setFullYear(minDriverAgeDate.getFullYear() - minDriverAge);
    const { currentEmployee  } = useSelector((state: RootState) => state.auth);

    useEffect(() => {
        const getData = async () => {
            const response = await OptionsAPI.get("years_passed_test");
            const yearsPassedList = response.map((option: any) => {
                return { value: option["id"], desc: option["description"]};
            });
            setOptions(yearsPassedList);
            const employeeData = { ...currentEmployee };
            if (employeeData?.dob) {
                employeeData.dob = new Date(Date.parse(employeeData.dob.toString()));
            }
            methods.reset(employeeData ?? {} as EmployeeType);
        }
        getData();
    }, []);
    


    const onSubmit: SubmitHandler<EmployeeType> = useDebouncedCallback(async (data) => {
        data.firstname_legal = currentEmployee?.firstname_legal ?? "";
        data.firstname_legal = currentEmployee?.firstname_legal ?? "";
        data.lastname_legal = currentEmployee?.lastname_legal ?? "";
        data.dob = data.dob instanceof Date ? getDateString(data.dob) : null;
        data.number_accidents = data.number_accidents ? data.number_accidents : 0;
        data.years_passed_test = data.years_passed_test ? data.years_passed_test : 0;
        await EmployeeAPI.update(instance, data);
        data.dob = new Date(data.dob ?? "");
        const message = "Details updated";
        dispatch(setCurrentEmployee(data));
        toast.success(message, { ...toastConfig, toastId: message + "-toast" });
    }, 300);

    return (
        <>
            <FormProvider {...methods}>
                <Form onSubmit={methods.handleSubmit(onSubmit)}>
                    <Header>Personal Details</Header>
                    <Table>
                        <tbody>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Title</FieldName>
                                <FieldDataCell>
                                    <SelectField name="title" options={titleOptions}/>
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>First Name</FieldName>
                                <FieldDataCell>
                                    <TextField name="firstname" required={true} maxlength={35} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>Surname</FieldName>
                                <FieldDataCell>
                                    <TextField name="lastname" required={true} maxlength={35} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>DOB</FieldName>
                                <FieldDataCell>
                                    <DateField name="dob" maxDate={minDriverAgeDate} minDate={maxDriverAgeDate} required={true} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>Home Address</FieldName>
                                <FieldDataCell>
                                    <TextField name="home_address1" required={true} maxlength={50} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>Home Address Line 2</FieldName>
                                <FieldDataCell>
                                    <TextField name="home_address2" maxlength={50} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>City/Town</FieldName>
                                <FieldDataCell>
                                    <TextField name="home_address3" required={true} maxlength={50} />
                                </FieldDataCell>
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>County</FieldName>
                                <FieldDataCell>
                                    <TextField name="home_address4" required={true} maxlength={50} />
                                </FieldDataCell>
                                
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Home Postcode</FieldName>
                                <FieldDataCell>
                                    <TextField name="home_postcode" maxlength={8} required={true} validation={validatePostcode} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>Home Country</FieldName>
                                <FieldDataCell>
                                    <SelectField name="home_country" required={true} options={countryOptions} />
                                </FieldDataCell>
                            </TableRow>

                            <TableRow $height={tableRowHeight}>
                                <FieldName>Home Tel</FieldName>
                                <FieldDataCell>
                                    <TextField name="home_tel" required={true} validation={validatePhone} />
                                </FieldDataCell>
                                
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Mobile Tel</FieldName>
                                <FieldDataCell>
                                    <TextField name="mobile_tel" validation={validatePhone} />
                                </FieldDataCell>
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Years Passed Test</FieldName>
                                <FieldDataCell>
                                    <SelectField 
                                        name="years_passed_test" 
                                        options={options}
                                    />
                                </FieldDataCell>
                                
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Number of Fault Accidents in the last 3 years</FieldName>
                                <FieldDataCell>
                                    <SelectField name="number_accidents" options={noAccidentsOptions}/>
                                </FieldDataCell>
                                
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Staff Number</FieldName>
                                <FieldDataCell>
                                    <TextField name="staff_number"/>
                                </FieldDataCell>
                                
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>NI Number</FieldName>
                                <FieldDataCell>
                                    <TextField name="ni_number" validation={validateNINumber} maxlength={9} />
                                </FieldDataCell>
                            </TableRow>
                            <TableRow $height={tableRowHeight}>
                                <FieldName>Marketing Emails</FieldName>
                                <FieldDataCell>
                                    <CheckBoxField 
                                        name="gdpr_receive_email" 
                                        label="I would like to recieve marketing emails" 
                                        defaultChecked={currentEmployee?.gdpr_receive_email}
                                    />
                                </FieldDataCell>
                            </TableRow>
                        </tbody>
                    </Table>
                    <Button colour="dark" type="submit" text="Update" />
                </Form>
            </FormProvider>
        </>
    );

}
export default PersonalDetails;
